"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BackgroundMusicSchemaShared = exports.CaptionsSchemaShared = exports.VideoInputBase = exports.ImageInputBase = exports.ProductShotInputBase = exports.VoiceInputBase = exports.ActorInputBase = exports.AgentAdConceptScenesShared = exports.UserProvidedStaticSceneSchemaShared = exports.GeneratedStaticSceneSchemaShared = exports.GeneratedBrollSceneSchemaShared = exports.UserProvidedBrollSceneSchemaShared = exports.SpeakingActorSceneSchemaShared = exports.AgentAdConceptSceneType = exports.UserProvidedStaticSceneParamsSchema = exports.GeneratedStaticSceneParamsSchema = exports.GeneratedBrollSceneParamsSchema = exports.UserProvidedBrollSceneParamsSchema = exports.SpeakingActorSceneParamsSchema = exports.BaseSceneSchemaNoUuid = exports.OverlaysSchema = exports.SpeakingActorOverlaySchema = exports.VideoOverlaySchema = exports.ImageOverlaySchema = exports.TextOverlaySchema = exports.AdOverlayType = exports.BaseOverlaySchema = exports.BoundsSchema = exports.TimeBoundsSchema = exports.TextualBoundsSchema = exports.PositionSchema = void 0;
const zod_1 = __importDefault(require("zod"));
// Shared position schema
exports.PositionSchema = zod_1.default.enum([
    'top-left',
    'top-center',
    'top-right',
    'center-left',
    'center',
    'center-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
]);
// Shared bounds schemas
exports.TextualBoundsSchema = zod_1.default.object({
    type: zod_1.default.literal('textual'),
    text: zod_1.default
        .string()
        .describe('The exact text that is spoken in this scene. The scene is visible for as long as this text is spoken'),
    voiceLabel: zod_1.default.string().nullable(),
});
exports.TimeBoundsSchema = zod_1.default.object({
    type: zod_1.default.literal('time'),
    duration: zod_1.default.number().min(0).describe('in seconds'),
});
exports.BoundsSchema = zod_1.default
    .union([exports.TextualBoundsSchema, exports.TimeBoundsSchema])
    .describe('Bounds define how long this scene is visible. If there is a voiceover in the background with spoken words, please use textual bounds. Otherwise, use time bounds with specified durations of each scene in seconds.');
// Shared overlay schemas (identical across LLM/non-LLM)
exports.BaseOverlaySchema = zod_1.default.object({
    bounds: exports.BoundsSchema,
    position: exports.PositionSchema,
});
var AdOverlayType;
(function (AdOverlayType) {
    AdOverlayType["Text"] = "text";
    AdOverlayType["Image"] = "image";
    AdOverlayType["Video"] = "video";
    AdOverlayType["SpeakingActor"] = "speaking-actor";
    AdOverlayType["MobileScreenRecording"] = "mobile-screen-recording";
})(AdOverlayType || (exports.AdOverlayType = AdOverlayType = {}));
exports.TextOverlaySchema = exports.BaseOverlaySchema.extend({
    type: zod_1.default.literal(AdOverlayType.Text),
    parameters: zod_1.default.object({
        text: zod_1.default.string(),
        styles: zod_1.default.object({
            fontSize: zod_1.default.number(),
            color: zod_1.default
                .string()
                .describe('The color of the text in hex format. Typically white (#FFFFFF) for outlined or black (#000000) or contrasted brand color for padded.'),
            textAlign: zod_1.default.enum(['left', 'center', 'right']).describe('By default, the text is centered'),
            styleType: zod_1.default
                .enum(['TIKTOK_OUTLINED', 'TIKTOK_PADDED'])
                .describe('Defines the text style and background appearance. "TIKTOK_OUTLINED" applies a contrasting outline around the text. "TIKTOK_PADDED" adds a background padding (like black or brand colors) with a contrasting text color to ensure readability'),
            outlineColor: zod_1.default
                .string()
                .describe('The color of the text outline when using "tiktok-outlined" style. Typically black (#000000) for white text or white (#FFFFFF) for black text. Do not use the same color as the text color.')
                .nullable(),
            paddingColor: zod_1.default
                .string()
                .describe('The background color of the padding when using "tiktok-padded" style. Typically white (#FFFFFF) or brand color. Do not use the same color as the text color.')
                .nullable(),
        }),
    }),
});
exports.ImageOverlaySchema = exports.BaseOverlaySchema.extend({
    type: zod_1.default.literal(AdOverlayType.Image),
    parameters: zod_1.default.object({
        imageLabel: zod_1.default.string(),
        styles: zod_1.default.object({
            width: zod_1.default.number(),
            height: zod_1.default.number(),
        }),
    }),
});
exports.VideoOverlaySchema = exports.BaseOverlaySchema.extend({
    type: zod_1.default.enum([AdOverlayType.Video, AdOverlayType.MobileScreenRecording]),
    parameters: zod_1.default.object({
        videoLabel: zod_1.default.string(),
        styles: zod_1.default.object({
            width: zod_1.default.number(),
            height: zod_1.default.number(),
        }),
    }),
});
exports.SpeakingActorOverlaySchema = exports.BaseOverlaySchema.extend({
    type: zod_1.default.literal(AdOverlayType.SpeakingActor),
    parameters: zod_1.default.object({
        actorLabel: zod_1.default.string(),
        styles: zod_1.default.object({
            width: zod_1.default.number(),
            height: zod_1.default.number(),
        }),
    }),
});
exports.OverlaysSchema = zod_1.default.union([
    exports.TextOverlaySchema,
    exports.ImageOverlaySchema,
    exports.VideoOverlaySchema,
    exports.SpeakingActorOverlaySchema,
]);
// Shared base scene (without uuid; non-LLM uses this as-is, non-LLM extends)
exports.BaseSceneSchemaNoUuid = zod_1.default.object({
    directorsNote: zod_1.default.string().nullable(),
    bounds: exports.BoundsSchema,
    overlays: zod_1.default.array(exports.OverlaysSchema),
});
// Shared scene-parameter schemas (identical across variants)
exports.SpeakingActorSceneParamsSchema = zod_1.default.object({
    prompt: zod_1.default
        .string()
        .nullable()
        .describe("What composition is in this scene? How are potential actors and items interacting? Please use labels with @ syntax to reference the products, actors, and other assets, e.g. '@actor-0 speaks into the camera, holding @product-0 in his hand. @image-0 is in the background"),
    actorLabel: zod_1.default
        .string()
        .nullable()
        .describe("Is a specific actor visible in this scene? Reference them using their label e.g. 'actor-0'"),
    productsShotLabels: zod_1.default
        .array(zod_1.default.string())
        .describe("Are there any products visible in this scene? Reference them using their label e.g. 'product-0'"),
    referenceImageFilesLabels: zod_1.default
        .array(zod_1.default.string())
        .describe("Are very specific non-product assets visible in this scene? Reference them using their label e.g. 'image-0'"),
});
exports.UserProvidedBrollSceneParamsSchema = zod_1.default.object({
    type: zod_1.default.enum([AdOverlayType.Video, AdOverlayType.MobileScreenRecording]),
    description: zod_1.default
        .string()
        .describe('Description of the broll which makes clear what is seen and what the point of this shot is. E.g. a mobile screen recording showing xyz'),
    videoFileLabel: zod_1.default.string().nullable(),
});
exports.GeneratedBrollSceneParamsSchema = zod_1.default.object({
    prompt: zod_1.default
        .string()
        .nullable()
        .describe("What composition is in this scene? How are potential actors and items interacting? Please use labels with @ syntax to reference the products, actors, and other assets, e.g. '@product-0 stands on the table in the kitchen, @actor-0 sits in front of a laptop, ...'. IMPORTANT: If this is the same setting with the same elements as in other parts of the video, ensure to use the exact same prompt so that the base asset can be reused!!!"),
    actorLabel: zod_1.default
        .string()
        .nullable()
        .describe("Is a specific actor visible in this scene? Reference them using their label e.g. 'actor-0'"),
    productsShotLabels: zod_1.default
        .array(zod_1.default.string())
        .describe("Are there any products visible in this scene? Reference them using their label e.g. 'product-0'"),
    referenceImageFilesLabels: zod_1.default
        .array(zod_1.default.string())
        .describe("Are very specific non-product assets visible in this scene or backgrounds the scene should be rendered in? Reference them using their label e.g. 'image-0'"),
});
exports.GeneratedStaticSceneParamsSchema = zod_1.default.object({
    description: zod_1.default
        .string()
        .describe('Description of the static which makes clear what is seen and what the point of this shot is. E.g. product screenshot / image that showcases feature xyz'),
    productsShotLabels: zod_1.default.array(zod_1.default.string()),
    referenceImageFilesLabels: zod_1.default.array(zod_1.default.string()),
});
exports.UserProvidedStaticSceneParamsSchema = zod_1.default.object({
    imageFileLabel: zod_1.default.string(),
});
var AgentAdConceptSceneType;
(function (AgentAdConceptSceneType) {
    AgentAdConceptSceneType["SpeakingActor"] = "speaking-actor";
    AgentAdConceptSceneType["UserProvidedBroll"] = "user-provided-broll";
    AgentAdConceptSceneType["GeneratedBroll"] = "generated-broll";
    AgentAdConceptSceneType["GeneratedStatic"] = "generated-static";
    AgentAdConceptSceneType["UserProvidedStatic"] = "user-provided-static";
})(AgentAdConceptSceneType || (exports.AgentAdConceptSceneType = AgentAdConceptSceneType = {}));
// Shared full scene schemas (without uuid)
exports.SpeakingActorSceneSchemaShared = exports.BaseSceneSchemaNoUuid.extend({
    type: zod_1.default.literal(AgentAdConceptSceneType.SpeakingActor),
    parameters: exports.SpeakingActorSceneParamsSchema,
}).describe('A scene where an actor is CLEARLY speaking into the camera. It is only a speaking actor scene if the actor actually speaks visually and the voiceover in the background matches her speak. It can only be a speaking actor video if is textual bound! If the actor does not move their lips to speak, its not a speaking actor scene but a GENERATED broll scene with that actor');
exports.UserProvidedBrollSceneSchemaShared = exports.BaseSceneSchemaNoUuid.extend({
    type: zod_1.default.literal(AgentAdConceptSceneType.UserProvidedBroll),
    parameters: exports.UserProvidedBrollSceneParamsSchema,
}).describe('A scene that is clearly user-provided and cannot be generated by a video diffusion model. Examples include: a mobile screen recording showing real app interactions,  a screen capture of a live video call that a generative model could not plausibly synthesize. A mobile screen recording can have multiple screens in one entity. So if the recording is coherent, then its just one entity here. If there is no cut between the screens, then its just one entity here, so if there is no other scene type interfering and its a coherent recording it is always just one entity. "Actors" cannot be inside of user provided content, only random other persons.');
exports.GeneratedBrollSceneSchemaShared = exports.BaseSceneSchemaNoUuid.extend({
    type: zod_1.default.literal(AgentAdConceptSceneType.GeneratedBroll),
    parameters: exports.GeneratedBrollSceneParamsSchema,
}).describe('A scene that is generated by a video diffusion model. Examples include: a actor sitting in a chair in their office, a actor holding a given energy drink can, a actor looking into the camera and posing WITHOUT speaking, or any scene that can be synthesized from a text prompt and reference materials, e.g. a product placed in a scene with the camera panning around it');
exports.GeneratedStaticSceneSchemaShared = exports.BaseSceneSchemaNoUuid.extend({
    type: zod_1.default.literal(AgentAdConceptSceneType.GeneratedStatic),
    parameters: exports.GeneratedStaticSceneParamsSchema,
}).describe('A static scene that can be well generated from a prompt and potential reference images. This includes illustrative scenes like a CTA screen for a TikTok video. ');
exports.UserProvidedStaticSceneSchemaShared = exports.BaseSceneSchemaNoUuid.extend({
    type: zod_1.default.literal(AgentAdConceptSceneType.UserProvidedStatic),
    parameters: exports.UserProvidedStaticSceneParamsSchema,
}).describe('A static scene that shows a specific image that is most likely to not be generated but user-provided. This includes screenshots, etc.');
exports.AgentAdConceptScenesShared = zod_1.default.union([
    exports.SpeakingActorSceneSchemaShared,
    exports.UserProvidedBrollSceneSchemaShared,
    exports.GeneratedBrollSceneSchemaShared,
    exports.GeneratedStaticSceneSchemaShared,
    exports.UserProvidedStaticSceneSchemaShared,
]);
// Shared inputs: base shapes (without parameters)
exports.ActorInputBase = zod_1.default.object({
    label: zod_1.default.string().describe('You have to give each actor a label, starting with actor-0, then actor-1, etc.'),
    description: zod_1.default
        .string()
        .describe('How does the actor look like? Detailed description, including age, gender, looks, etc.'),
});
exports.VoiceInputBase = zod_1.default.object({
    label: zod_1.default.string().describe('You have to give each voice a label, starting with voice-0, then voice-1, etc.'),
    description: zod_1.default.string().describe('How does the voice sound like? Very concise description.'),
});
exports.ProductShotInputBase = zod_1.default
    .object({
    label: zod_1.default
        .string()
        .describe('You have to give each product a label, starting with product-0, then product-1, etc.'),
    description: zod_1.default.string().describe('How does the product look like? Concise description.'),
})
    .describe('This object should contain all e-commerce products in the video that are likely to be the protagonist product. Detect those products once and store them here, then mention them in the scenes where they appear. This must only be the exact advertised product. If there are other objects (or comparables) in the video, they must be described in a textual way but not as a product! If a product consists of multiple elements that are shown independent of each other, it most be labeled as MULTIPLE products. E.g. a starter kit box vs the contents of the box.');
exports.ImageInputBase = zod_1.default.object({
    label: zod_1.default.string().describe('You have to give each image a label, starting with image-0, then image-1, etc.'),
    description: zod_1.default.string(),
});
exports.VideoInputBase = zod_1.default.object({
    label: zod_1.default.string().describe('You have to give each video a label, starting with video-0, then video-1, etc.'),
    description: zod_1.default.string(),
});
// Shared misc (identical)
exports.CaptionsSchemaShared = zod_1.default.object({
    enabled: zod_1.default.boolean(),
    style: zod_1.default.enum(['default']).default('default'),
});
exports.BackgroundMusicSchemaShared = zod_1.default.object({
    description: zod_1.default
        .string()
        .describe('Describe the background music in professional terms, so that it could be reproduced by a professional music producer.'),
    bounds: zod_1.default
        .object({
        startSceneIndex: zod_1.default.number(),
        endSceneIndex: zod_1.default.number().nullable(),
    })
        .describe('Music is bound by scenes. So it starts a scene and ends at the certain scene (or the last scene)'),
});
